import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { suppliersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Search, Plus, Trash2 } from 'lucide-react'

const supplierFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  document: z.string().min(1, 'Documento é obrigatório'),
  type: z.enum(['MATERIALS', 'SERVICES', 'BOTH'], { required_error: 'Tipo é obrigatório' }),
  rating: z.coerce
    .number()
    .min(0, 'Avaliação mínima é 0')
    .max(5, 'Avaliação máxima é 5')
    .optional()
    .or(z.literal('')),
  address: z.string().optional(),
  website: z.string().optional(),
})

type SupplierFormValues = z.infer<typeof supplierFormSchema>

interface ContactItem {
  name: string
  role: string
  phone: string
  email: string
}

interface SupplierFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplier?: any
}

export function SupplierFormDialog({
  open,
  onOpenChange,
  supplier,
}: SupplierFormDialogProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId')
  const isEdit = !!supplier

  const maskDocument = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14)
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
  }, [])

  const buildInitialContactList = (): ContactItem[] => {
    if (!supplier?.contacts) return []
    if (supplier.contacts.list?.length) {
      return supplier.contacts.list.map((c: any) => ({
        name: c.name ?? '',
        role: c.role ?? '',
        phone: c.phone ?? '',
        email: c.email ?? '',
      }))
    }
    if (supplier.contacts.contactPerson) {
      return [{
        name: supplier.contacts.contactPerson,
        role: '',
        phone: supplier.contacts.phone ?? '',
        email: supplier.contacts.email ?? '',
      }]
    }
    return []
  }

  const [contactList, setContactList] = useState<ContactItem[]>(buildInitialContactList)
  const [isActive, setIsActive] = useState<boolean>(supplier?.isActive ?? true)

  const form = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierFormSchema),
    defaultValues: supplier
      ? {
          name: supplier.name,
          document: maskDocument(supplier.document || ''),
          type: supplier.type,
          rating: supplier.rating ?? '',
          address: supplier.contacts?.address || '',
          website: supplier.contacts?.website || '',
        }
      : {
          name: '',
          document: '',
          type: undefined as any,
          rating: '',
          address: '',
          website: '',
        },
  })

  const [cnpjMetadata, setCnpjMetadata] = useState<any>(null)

  const cnpjLookupMutation = useMutation({
    mutationFn: (cnpj: string) => suppliersAPI.lookupCnpj(cnpj),
    onSuccess: (data: any) => {
      const addressParts = [
        data.logradouro,
        data.numero,
        data.complemento,
        data.bairro,
        data.municipio,
        data.uf,
        data.cep,
      ].filter(Boolean)

      form.setValue('name', data.nomeFantasia || data.razaoSocial)
      if (addressParts.length > 0) form.setValue('address', addressParts.join(', '))
      if (data.email || data.telefone1 || data.qsa?.length > 0) {
        setContactList([{
          name: data.qsa?.[0]?.nome ?? '',
          role: '',
          phone: data.telefone1 ?? '',
          email: data.email ?? '',
        }])
      }

      setCnpjMetadata({
        razaoSocial: data.razaoSocial,
        nomeFantasia: data.nomeFantasia,
        cnaeFiscal: data.cnaeFiscal,
        cnaeFiscalDescricao: data.cnaeFiscalDescricao,
        cnaesSecundarios: data.cnaesSecundarios,
        porte: data.porte,
        situacaoCadastral: data.situacaoCadastral,
        descricaoSituacaoCadastral: data.descricaoSituacaoCadastral,
        capitalSocial: data.capitalSocial,
        naturezaJuridica: data.naturezaJuridica,
        dataInicioAtividade: data.dataInicioAtividade,
        qsa: data.qsa,
        opcaoPeloSimples: data.opcaoPeloSimples,
        opcaoPeloMei: data.opcaoPeloMei,
      })

      toast.success('Dados do CNPJ preenchidos!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao consultar CNPJ: ${error.message}`)
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: SupplierFormValues) => {
      const payload: any = {
        name: values.name,
        document: values.document.replace(/\D/g, ''),
        type: values.type,
        rating:
          values.rating !== '' && values.rating !== undefined
            ? Number(values.rating)
            : undefined,
        contacts: {
          address: values.address || undefined,
          website: values.website || undefined,
          list: contactList
            .filter((c) => c.name.trim())
            .map((c) => ({
              name: c.name.trim(),
              role: c.role.trim() || undefined,
              phone: c.phone.trim() || undefined,
              email: c.email.trim() || undefined,
            })),
        },
        ...(cnpjMetadata && {
          dataSource: 'AUTO_CNPJ',
          metadata: cnpjMetadata,
        }),
        ...(isEdit && { isActive }),
      }

      if (isEdit) {
        return suppliersAPI.update(supplier.id, payload)
      }
      return suppliersAPI.create(payload)
    },
    onSuccess: async (data: any) => {
      // Auto-link to project if projectId in query params
      if (projectId && !isEdit) {
        try {
          const newSupplierId = data.id
          await suppliersAPI.assignToProject(newSupplierId, projectId, {
            role: data.type,
            notes: '',
          })
          // Invalidate project data to update doneCheck in Dashboard
          queryClient.invalidateQueries({ queryKey: ['projects'] })
          queryClient.invalidateQueries({ queryKey: ['project', projectId] })
        } catch (error) {
          console.error('Failed to link supplier to project:', error)
        }
      }

      toast.success(
        isEdit ? 'Fornecedor atualizado!' : 'Fornecedor cadastrado!'
      )
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      if (isEdit) {
        queryClient.invalidateQueries({
          queryKey: ['supplier', supplier.id],
        })
      }
      onOpenChange(false)
      form.reset()

      // Redirect to Dashboard if came from projectId context
      if (projectId && !isEdit) {
        navigate('/?openPhase=2')
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: SupplierFormValues) => {
    mutation.mutate(values)
  }

  const typeLabels: Record<string, string> = {
    MATERIALS: 'Materiais',
    SERVICES: 'Serviços',
    BOTH: 'Ambos',
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informações do fornecedor.'
              : 'Preencha as informações para cadastrar um novo fornecedor.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Informações Básicas
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="Nome do fornecedor"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="document">Documento (CPF/CNPJ) *</Label>
                <div className="flex gap-2">
                  <Input
                    id="document"
                    {...form.register('document')}
                    placeholder="00.000.000/0001-00"
                    className="flex-1"
                    onChange={(e) => {
                      form.setValue('document', maskDocument(e.target.value), { shouldValidate: false })
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={cnpjLookupMutation.isPending}
                    onClick={() => {
                      const doc = form.getValues('document').replace(/\D/g, '')
                      if (doc.length === 14) {
                        cnpjLookupMutation.mutate(doc)
                      } else {
                        toast.error('Digite um CNPJ completo (14 digitos) para buscar')
                      }
                    }}
                  >
                    {cnpjLookupMutation.isPending ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="mr-1 h-4 w-4" />
                    )}
                    Buscar
                  </Button>
                </div>
                {form.formState.errors.document && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.document.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="type">Tipo *</Label>
                <Select
                  value={form.watch('type')}
                  onValueChange={(value) => form.setValue('type', value as any)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.type && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.type.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="rating">Avaliação (0-5)</Label>
                <Input
                  id="rating"
                  type="number"
                  min={0}
                  max={5}
                  step={0.5}
                  {...form.register('rating')}
                  placeholder="0 a 5"
                />
                {form.formState.errors.rating && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.rating.message}
                  </p>
                )}
              </div>

              {isEdit && (
                <div className="flex items-center justify-between rounded-lg border p-3 col-span-2">
                  <div>
                    <p className="text-sm font-medium">Status do fornecedor</p>
                    <p className="text-xs text-neutral-500">
                      {isActive ? 'Ativo — aparece nas buscas e pedidos' : 'Inativo — oculto nas buscas'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={isActive ? 'default' : 'outline'}
                    className="min-w-[90px]"
                    onClick={() => setIsActive(!isActive)}
                  >
                    {isActive ? 'Ativo' : 'Inativo'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Contacts */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Contato</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="address">Endereço</Label>
                <Input
                  id="address"
                  {...form.register('address')}
                  placeholder="Endereço completo"
                />
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  {...form.register('website')}
                  placeholder="https://www.exemplo.com.br"
                />
              </div>
            </div>

            {/* Contact list */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-neutral-600">Pessoas de Contato</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1"
                  onClick={() => setContactList((prev) => [...prev, { name: '', role: '', phone: '', email: '' }])}
                >
                  <Plus className="h-3.5 w-3.5" /> Adicionar
                </Button>
              </div>

              {contactList.length === 0 && (
                <p className="text-xs text-neutral-400 py-1">Nenhuma pessoa de contato adicionada.</p>
              )}

              {contactList.map((contact, idx) => (
                <div key={idx} className="rounded-lg border bg-neutral-50 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-500">Contato {idx + 1}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-neutral-400 hover:text-red-500"
                      onClick={() => setContactList((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Nome *</Label>
                      <Input
                        value={contact.name}
                        onChange={(e) => setContactList((prev) => prev.map((c, i) => i === idx ? { ...c, name: e.target.value } : c))}
                        placeholder="Nome da pessoa"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cargo / Departamento</Label>
                      <Input
                        value={contact.role}
                        onChange={(e) => setContactList((prev) => prev.map((c, i) => i === idx ? { ...c, role: e.target.value } : c))}
                        placeholder="Ex: Vendas, Financeiro"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Telefone</Label>
                      <Input
                        value={contact.phone}
                        onChange={(e) => setContactList((prev) => prev.map((c, i) => i === idx ? { ...c, phone: e.target.value } : c))}
                        placeholder="(00) 00000-0000"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">E-mail</Label>
                      <Input
                        type="email"
                        value={contact.email}
                        onChange={(e) => setContactList((prev) => prev.map((c, i) => i === idx ? { ...c, email: e.target.value } : c))}
                        placeholder="email@exemplo.com"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? 'Salvar' : 'Cadastrar Fornecedor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
